import { useState } from 'react';
import { useAppSelector } from '@/store/hooks';
import { useOpsSettings, useSendTestPage, useUpdateOpsSettings } from '@/services/queries';
import { canRelease } from '@/types';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Skeleton } from '@/shared/ui/skeleton';
import { Switch } from '@/shared/ui/switch';
import { formatDateTime } from '@/shared/lib/format';
import { SettingsGroup, SettingsRow } from './SettingsGroup';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Who hears about it now: the on-call page (an auto-halt, or the first fatal
 * report of a crash in a day), and how long desktop logs are kept (fixed).
 */
export function OncallPaging() {
  const settings = useOpsSettings();
  const update = useUpdateOpsSettings();
  const testPage = useSendTestPage();
  const mayChange = canRelease(useAppSelector((state) => state.auth.user?.role));

  const stored = settings.data?.oncallEmail ?? '';
  // What is being typed; null until the field is touched, so it shows the saved
  // address (and follows it after a save) without copying it into state.
  const [draft, setDraft] = useState<string | null>(null);
  const email = draft ?? stored;

  if (!settings.data) {
    return (
      <SettingsGroup title="On-call and logs" sub="Who is paged, and what is kept">
        <div className="px-7 py-5">
          <Skeleton className="h-16 w-full" />
        </div>
      </SettingsGroup>
    );
  }

  const data = settings.data;
  const busy = !mayChange || update.isPending;
  const changed = email.trim() !== stored;
  const valid = email.trim() === '' || EMAIL.test(email.trim());

  return (
    <SettingsGroup title="On-call and logs" sub="Who is paged, and what is kept">
      <SettingsRow
        label="On-call email"
        desc="Where pages go. Send a test page to check it arrives before turning paging on."
      >
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Input
            type="email"
            value={email}
            onChange={(event) => {
              setDraft(event.target.value);
            }}
            placeholder="oncall@nuvo.ai"
            className="h-9 w-72"
            disabled={busy}
            aria-label="On-call email"
            aria-invalid={!valid}
          />
          <Button
            size="sm"
            disabled={busy || !changed || !valid}
            onClick={() => {
              update.mutate(
                { oncallEmail: email.trim() || null },
                {
                  onSuccess: () => {
                    setDraft(null);
                  },
                },
              );
            }}
          >
            Save
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={!mayChange || !stored || changed || testPage.isPending}
            onClick={() => {
              testPage.mutate();
            }}
          >
            Send test page
          </Button>
        </div>
      </SettingsRow>
      <SettingsRow
        label="Page on-call"
        desc="Emails the on-call address when a rollout is auto-halted, and on the first fatal report of a crash each day."
        control={
          <Switch
            checked={data.pageOncallOnCritical}
            disabled={busy || (!data.pageOncallOnCritical && !stored)}
            onCheckedChange={(next) => {
              update.mutate({ pageOncallOnCritical: next });
            }}
            aria-label="Page on-call"
          />
        }
      />
      <SettingsRow
        label="Desktop logs kept 90 days"
        desc="Error lines on the Logs page, and the full log files backed up to S3. Older ones are deleted automatically."
      />
      <p className="px-7 pb-4 text-[12px] text-muted-foreground">
        {data.updatedBy
          ? `Last changed by ${data.updatedBy.name}, ${formatDateTime(data.updatedAt)}.`
          : 'Defaults - not changed yet.'}
        {!mayChange && ' Changing these needs superadmin or engineering.'}
      </p>
    </SettingsGroup>
  );
}
